import './styles/InputRadioButton.scss';

import { ChangeEvent, HTMLAttributes } from 'react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks/useClassHelper';
import { InputErrorMessage } from '../InputErrorMessage/InputErrorMessage';

interface DefaultVariantProps extends HTMLAttributes<HTMLInputElement> {
  variant: 'primitive';
  id: string;
  label: string;
  name: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

interface ValueLabelVariantProps {
  variant?: 'value-label';
  values: { label: string; value: string }[] | [];
  error?: boolean;
  errorMessage?: string;
  groupName: string;
  onChange: (e: any) => void;
  checked?: boolean;
  checkedValue?: string;
  type: string;
  name: string;
}

type Props = ValueLabelVariantProps | DefaultVariantProps;

const componentName = 'c-InputRadioButton';

export const InputRadioButton = (props: Props) => {
  const c = useClassHelper(componentName);

  const { variant } = props;

  if (variant === 'value-label') {
    const { error, values, errorMessage, groupName, onChange, checked } = props;

    const getErrorClass = () => (error ? c('__value-label--error') : '');

    const activeClasseNames = [`${componentName}__value-label`, getErrorClass()]
      .filter(Boolean)
      .join(' ');

    return (
      <div>
        <InputErrorMessage
          id={`${groupName}ErrorMessage`}
          error={error}
          className=""
          errorMessage={errorMessage}
        />
        {values.map((val: any, index: any) => (
          <div
            className={`${activeClasseNames} ${cn('flex items-center')}`}
            key={`RadioButton_[${index}]`}
          >
            <input
              checked={checked || props?.checkedValue === val?.value}
              id={`RadioButton_${groupName}_${val.value}`}
              type="radio"
              value={val.value}
              name={groupName}
              onChange={onChange}
              className="focus-visible:focus-ring-form"
            />
            {val.label}
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'primitive') {
    const { onChange, id, name, label, checked, ...rest } = props;

    return (
      <label htmlFor={id} className={c('__primitive')}>
        <input
          id={id}
          type="radio"
          name={name}
          checked={checked}
          onChange={onChange}
          aria-checked={checked}
          aria-labelledby={`${id}-label`}
          className="focus-visible:focus-ring-form"
          {...rest}
        />
        <span id={`${id}-label`}>{label}</span>
      </label>
    );
  }

  return null;
};
